import React, { useState, useEffect } from 'react';
import { cleanCategoryName, formatYAxisNumber } from '../utils';
import { useNavigate } from 'react-router-dom';


import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';

// ─── STAT CARD (compact version) ─────────────────────────────────────────────
const StatCard = ({ label, value, sub, change, changeColor, icon }) => {
  const isPositive = changeColor === '#27ae60';
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e8edf2',
      borderRadius: 12,
      padding: '18px 20px',
      flex: 1,
      minWidth: 200,
      transition: 'box-shadow 0.25s ease, transform 0.2s ease',
      cursor: 'default',
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 14, color: '#888', fontWeight: 600, textTransform: 'none', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{label}</div>
</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.1, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>{sub}</div>
      {change && (
        <div style={{ fontSize: 13, color: changeColor || '#27ae60', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ fontSize: 12 }}>{isPositive ? '▲' : '▼'}</span> {change}
        </div>
      )}
    </div>
  );
};

// ─── PIE LABEL LAYOUT ─────────────────────────
const RADIAN = Math.PI / 180;

const buildPieLabelLayout = (data, cx, cy, outerRadius) => {
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const labelRadius = outerRadius + 28;
  const bendOffset = 12;
  const minGap = 15;

  let cumulative = 0;
  const items = data.map((d) => {
    const startAngle = (cumulative / total) * 360;
    cumulative += d.value;
    const endAngle = (cumulative / total) * 360;
    const midAngle = (startAngle + endAngle) / 2;
    const percent = d.value / total;

    const edgeX = cx + outerRadius * Math.cos(-midAngle * RADIAN);
    const edgeY = cy + outerRadius * Math.sin(-midAngle * RADIAN);
    const rawY = cy + labelRadius * Math.sin(-midAngle * RADIAN);
    const side = Math.cos(-midAngle * RADIAN) >= 0 ? 'right' : 'left';

    return { name: d.name, color: d.color, percent, edgeX, edgeY, rawY, side };
  });

  ['left', 'right'].forEach((side) => {
    const group = items
      .map((item, i) => ({ ...item, i }))
      .filter((item) => item.side === side)
      .sort((a, b) => a.rawY - b.rawY);

    for (let k = 1; k < group.length; k++) {
      if (group[k].rawY - group[k - 1].rawY < minGap) {
        group[k].rawY = group[k - 1].rawY + minGap;
      }
    }
    group.forEach((item) => { items[item.i].finalY = item.rawY; });
  });

  items.forEach((item) => {
    if (item.finalY === undefined) item.finalY = item.rawY;
    item.finalX = item.side === 'right' ? cx + labelRadius : cx - labelRadius;
    item.bendX = item.side === 'right' ? cx + outerRadius + bendOffset : cx - outerRadius - bendOffset;
  });

  return items;
};

// ─── LOADING SKELETON ────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div style={{
    background: '#fff',
    border: '1px solid #e8edf2',
    borderRadius: 12,
    padding: '18px 20px',
    flex: 1,
    minWidth: 0,
  }}>
    <div style={{ width: '60%', height: 10, background: '#f0f0f0', borderRadius: 4, marginBottom: 12 }} />
    <div style={{ width: '40%', height: 24, background: '#f0f0f0', borderRadius: 4, marginBottom: 8 }} />
    <div style={{ width: '50%', height: 10, background: '#f0f0f0', borderRadius: 4 }} />
  </div>
);

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────





const FinanceHomeSummary = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('1yr');
  const [kpiData, setKpiData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filters = ['1mo', '3mo', '6mo', '1yr'];
  const filterLabels = { '1mo': 'Last Month', '3mo': 'Last 3 Months', '6mo': 'Last 6 Months', '1yr': 'Last Year' };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const API = process.env.REACT_APP_BASE_API_URL || 'https://searchmytenders.in';
        const res = await fetch(`${API}/api/finance-analytics/kpis?filter=${activeFilter}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setKpiData(data.kpiData);
          if (data.chartData) {
            let parsedData = data.chartData;
            if (activeFilter === '1yr' || activeFilter === '6mo') {
              const monthsCount = activeFilter === '1yr' ? 12 : 6;
              const now = new Date();
              const padded = [];
              for (let i = monthsCount - 1; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const label = d.toLocaleString('default', { month: 'short', year: '2-digit' });
                const existing = parsedData.find(r => r.month === label);
                padded.push(existing || { month: label, Revenue: 0, Outstanding: 0 });
              }
              parsedData = padded;
            }
            setChartData(parsedData);
          }
          if (data.pieChartData) setPieChartData(data.pieChartData);
        } else {
          setError(data.message || 'Failed to load data');
        }
      } catch (err) {
        console.error('Failed to fetch finance home data', err);
        setError('Network error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeFilter]);

  const renderCustomLabel = (props) => {
    const { cx, cy, outerRadius, index, percent } = props;
    if (percent < 0.005) return null;

    const layout = buildPieLabelLayout(pieChartData, cx, cy, outerRadius);
    const pos = layout[index];
    if (!pos) return null;

    const _n = cleanCategoryName(pos.name); const shortName = _n.length > 16 ? _n.slice(0, 14) + '…' : _n;
    const anchor = pos.side === 'right' ? 'start' : 'end';
    const textX = pos.finalX + (pos.side === 'right' ? 6 : -6);

    return (
      <g>
        <polyline
          points={`${pos.edgeX},${pos.edgeY} ${pos.bendX},${pos.finalY} ${pos.finalX},${pos.finalY}`}
          fill="none"
          stroke="#c7c7c7"
          strokeWidth={1}
        />
        <circle cx={pos.finalX} cy={pos.finalY} r={2} fill={pos.color || '#999'} />
        <text x={textX} y={pos.finalY} textAnchor={anchor} dominantBaseline="central" style={{ fontSize: 9.5 }}>
          <tspan fill="#555">{shortName}</tspan>
          <tspan fill="#1a1a2e" fontWeight={700}>{`  ${(pos.percent * 100).toFixed(0)}%`}</tspan>
        </text>
      </g>
    );
  };

  return (
    <div style={{
      fontFamily: "'Segoe UI', sans-serif",
      color: '#1a1a2e',
      padding: '28px 32px 20px',
      maxWidth: 1400,
      margin: '0 auto',
    }}>

      <style>{`
        .recharts-wrapper svg,
        .recharts-wrapper svg * {
          outline: none !important;

        }
      `}</style>

      {/* ── HEADER + TIME FILTER ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div>
          <h4 style={{
            fontWeight: 700,
            fontSize: 22,
            marginBottom: 4,
            color: '#1a1a2e',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
Finance Performance Overview
          </h4>
          <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
            Revenue and collections summary — {filterLabels[activeFilter]}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {filters.map(f => (
            <button
              key={f}
              onClick={() => {
                if (activeFilter !== f) {
                  setLoading(true);
                  setActiveFilter(f);
                }
              }}
              style={{
                padding: '6px 18px',
                border: activeFilter === f ? '2px solid #00897b' : '1.5px solid #e0e0e0',
                borderRadius: '20px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                background: activeFilter === f ? '#e0f5f3' : '#fff',
                color: activeFilter === f ? '#00897b' : '#888',
                transition: 'all 0.2s',
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI STAT CARDS ── */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        {loading || !kpiData ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            <StatCard
              label="Total Revenue"
              value={kpiData.totalRevenue?.value}
              sub={`vs previous ${activeFilter}`}
              change={`${kpiData.totalRevenue?.change}%`}
              changeColor={kpiData.totalRevenue?.change >= 0 ? '#27ae60' : '#e74c3c'}
              icon="💰"
            />
            <StatCard
              label="Outstanding Amount"
              value={kpiData.outstanding?.value}
              sub={`vs previous ${activeFilter}`}
              change={`${kpiData.outstanding?.change}%`}
              changeColor={kpiData.outstanding?.change <= 0 ? '#27ae60' : '#e74c3c'}
            />
            <StatCard
              label="Total Invoices"
              value={kpiData.totalInvoices?.value}
              sub={`vs previous ${activeFilter}`}
              change={`${kpiData.totalInvoices?.change}%`}
              changeColor={kpiData.totalInvoices?.change >= 0 ? '#27ae60' : '#e74c3c'}
              icon="📄"
            />
            <StatCard
              label="Collection Success Rate"
              value={kpiData.successRate?.value}
              sub={`vs previous ${activeFilter}`}
              change={`${kpiData.successRate?.change}%`}
              changeColor={kpiData.successRate?.change >= 0 ? '#27ae60' : '#e74c3c'}
            />
            <StatCard
              label="Total Customers"
              value={kpiData.customers?.total || 0}
              sub={`${kpiData.customers?.paid || 0} paid, ${kpiData.customers?.unpaid || 0} unpaid`}
              icon="👥"
            />
          </>
        )}
      </div>

      {/* ── CHARTS ROW ── */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap', alignItems: 'stretch' }}>
        
        {/* REVENUE OVER TIME (Area Chart) */}
        <div style={{ flex: '1 1 60%', minWidth: '240px', background: '#fff', border: '1px solid #e8edf2', borderRadius: 12, padding: '20px 24px', minHeight: 320 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>Revenue & Outstanding Trend</div>
              <div style={{ fontSize: 11, color: '#aaa' }}>Performance over the {filterLabels[activeFilter].toLowerCase()}</div>
            </div>
            <button
              onClick={() => navigate('/finance-analytics')}
              style={{ background: 'none', border: 'none', color: '#3498db', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '4px 8px' }}
            >
              View Full Analytics →
            </button>
          </div>
          
          <div style={{ flex: 1, minHeight: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
            ) : error ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#e74c3c', fontSize: 12, fontWeight: 500 }}>{error}</div>
            ) : chartData.length === 0 ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>No data available</div>
            ) : (
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 15, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevHome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#27ae60" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#27ae60" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorOutHome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#e74c3c" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#e74c3c" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="month" tick={{ dy: 10, fontSize: 16, fill: '#999' }} axisLine={false} tickLine={false}  interval={activeFilter === '1mo' ? 2 : 'preserveStartEnd'} />
                <YAxis tick={{ dx: -10, fontSize: 16, fill: '#999' }} axisLine={false} tickLine={false} tickFormatter={(val) => `₹${(val/1000)}k`} />
                <Tooltip contentStyle={{ fontSize: 13, borderRadius: 6, border: '1px solid #e0e0e0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} formatter={(value) => `₹${value.toLocaleString()}`} />
                <Legend iconSize={18} wrapperStyle={{ fontSize: 14, paddingTop: 8 }} />
                <Area type="monotone" dataKey="Revenue" stroke="#27ae60" strokeWidth={2} fill="url(#colorRevHome)" dot={false} />
                <Area type="monotone" dataKey="Outstanding" stroke="#e74c3c" strokeWidth={2} fill="url(#colorOutHome)" dot={false} />
              </AreaChart>
            )}
          </ResponsiveContainer>
          </div>
        </div>

        {/* INVOICE STATUS (Pie Chart) */}
        <div style={{ flex: '1 1 35%', minWidth: '240px', background: '#fff', border: '1px solid #e8edf2', borderRadius: 12, padding: '20px 24px', minHeight: 320 }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>Invoice Status</div>
            <div style={{ fontSize: 11, color: '#aaa' }}>Current billing breakdown</div>
          </div>
          
          <div style={{ flex: 1, minHeight: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
            ) : error ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#e74c3c', fontSize: 12, fontWeight: 500 }}>{error}</div>
            ) : pieChartData.length === 0 ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>No data available</div>
            ) : (
              <PieChart margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={65}
                  outerRadius={85}
                  dataKey="value"
                  labelLine={false}
                  label={renderCustomLabel}
                  stroke="none"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || '#ccc'} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [value, name]}
                  contentStyle={{ fontSize: 11, borderRadius: 6, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                />
              </PieChart>
            )}
          </ResponsiveContainer>
          </div>
        </div>

      </div>

    </div>
  );
};

export default FinanceHomeSummary;
